(() => {
  const VIEW_THRESHOLD_MS = 1500;
  const ATTENTION_THRESHOLD_MS = 3000;
  const ATTENTION_FLUSH_MS = 15000;
  const MIN_ATTENTION_SEGMENT_MS = 1000;
  const SESSION_STORAGE_KEY = 'mb_measurement_session_v1';

  function clean(value, maxLength = 100) {
    return String(value ?? '').replace(/\s\s+/g, ' ').trim().slice(0, maxLength);
  }

  function measurementSessionId() {
    const now = Date.now();
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(SESSION_STORAGE_KEY) || 'null');
      if (saved?.id && now - Number(saved.touchedAt || 0) < 30 * 60 * 1000) {
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id: saved.id, touchedAt: now }));
        return clean(saved.id, 80);
      }
      const id = window.crypto?.randomUUID?.()
        || `${now.toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id, touchedAt: now }));
      return id;
    } catch (error) {
      return `${now.toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
    }
  }

  function initialize(root) {
    if (!root || root.dataset.mbContentAnalyticsReady === 'true') return;

    let config;
    try {
      config = JSON.parse(root.querySelector('[data-mb-clean-product]')?.textContent || '{}');
    } catch (error) {
      return;
    }

    const trackingConfig = config?.trackingConfig;
    if (!trackingConfig || Number(trackingConfig.version) < 1 || trackingConfig.liveTracking !== true) return;

    const product = config.product || {};
    const sessionId = measurementSessionId();
    const state = {
      observer: null,
      timers: new Map(),
      tracked: new Set(),
      sections: [],
      activeSection: null,
      activeStartedAt: null,
      pendingAttentionMs: new Map(),
      attentionFlushTimer: null,
      attentionRaf: null,
      resizeRaf: null,
      selectedVariant: window.MagBakPdpVariant?.variant
        || product.variants?.find((variant) => String(variant.id) === String(config.currentVariantId))
        || product.variants?.[0]
        || null,
    };
    const storageKey = `mb_content_sections:${product.handle}:${sessionId}`;
    const production = /(^|\.)magbak\.com$/i.test(window.location.hostname)
      && !new URLSearchParams(window.location.search).has('preview_theme_id')
      && !window.Shopify?.designMode;

    root.dataset.mbContentAnalyticsReady = 'true';

    try {
      const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || '[]');
      if (Array.isArray(saved)) {
        state.tracked = new Set(saved.map((value) => clean(value, 100)).filter(Boolean));
      }
    } catch (error) {}

    function persistTrackedSections() {
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify([...state.tracked]));
      } catch (error) {}
    }

    function publishContentEvent(eventName, item, extra = {}, retry = 0) {
      if (!production) return;
      if (typeof window.Shopify?.analytics?.publish !== 'function') {
        if (retry < 20) {
          window.setTimeout(() => publishContentEvent(eventName, item, extra, retry + 1), 250);
        }
        return;
      }

      const variant = state.selectedVariant || {};
      const selectedOptions = (variant.options || [])
        .filter((value) => value && value !== 'Default Title')
        .join(' | ');
      try {
        window.Shopify.analytics.publish('magbak_experiments:event', {
          event_name: eventName,
          experiment_id: 'no_experiment',
          experiment_variant: 'live',
          experiment_unit_id: '',
          measurement_session_id: sessionId,
          gallery_ui_version: 'swipeable_thumbnails_v1',
          product_id: String(product.id || ''),
          product_handle: clean(product.handle, 100),
          product_title: clean(product.title, 100),
          variant_id: String(variant.id || ''),
          variant_sku: clean(variant.sku, 100),
          selected_options: clean(selectedOptions, 100),
          content_section_key: item.key,
          content_section_label: item.label,
          content_section_index: item.index,
          content_section_count: item.count,
          content_section_version: item.version,
          content_depth_ratio: item.count ? item.index / item.count : 0,
          content_view_threshold_ms: VIEW_THRESHOLD_MS,
          ...extra,
        });
      } catch (error) {}
    }

    function publishContentView(item) {
      publishContentEvent('pdp_content_section_view', item);
    }

    function publishContentAttention(item, activeMs) {
      publishContentEvent('pdp_content_section_attention', item, {
        content_active_ms: Math.round(activeMs),
        content_attention_threshold_ms: ATTENTION_THRESHOLD_MS,
      });
    }

    function sectionKey(section, index) {
      const wrapperId = section.closest('.shopify-section')?.id || section.id || '';
      const stableId = wrapperId.includes('__') ? wrapperId.split('__').pop() : wrapperId;
      return clean(stableId || `section-${index + 1}`, 100);
    }

    function sectionLabel(section, index) {
      if (section.matches('[data-mb-judgeme-mirror], .mb-reviews')) return 'Customer reviews';
      const heading = section.querySelector(
        '[data-mb-content-analytics-label], h2:not([data-mb-review-rating]), .mb-pdp-section__header h3'
      );
      const eyebrow = section.querySelector('.mb-pdp-eyebrow, .mb-reviews__eyebrow');
      const label = clean(heading?.textContent || eyebrow?.textContent || '', 100);
      if (label) return label;
      const fallback = sectionKey(section, index)
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());
      return clean(fallback || `Section ${index + 1}`, 100);
    }

    function sectionVersion(section) {
      const content = clean(section.textContent, 2000);
      let hash = 2166136261;
      for (let index = 0; index < content.length; index += 1) {
        hash ^= content.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return `v1-${(hash >>> 0).toString(36)}`;
    }

    function sectionIsEligible(section) {
      if (!section || section.hidden || section.getAttribute('aria-hidden') === 'true') return false;
      if (section.closest('[hidden], [aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(section);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      const rect = section.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    function hasMeaningfulVisibility(section) {
      if (document.visibilityState !== 'visible' || !sectionIsEligible(section)) return false;
      const rect = section.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const requiredHeight = Math.max(48, Math.min(rect.height * 0.5, window.innerHeight * 0.35));
      const requiredWidth = Math.max(48, Math.min(rect.width * 0.5, window.innerWidth * 0.5));
      return visibleHeight >= requiredHeight && visibleWidth >= requiredWidth;
    }

    function cancelTimer(section) {
      const timer = state.timers.get(section);
      if (timer) window.clearTimeout(timer);
      state.timers.delete(section);
    }

    function visibleArea(section) {
      if (!sectionIsEligible(section)) return 0;
      const rect = section.getBoundingClientRect();
      const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      return height * width;
    }

    function pauseActiveSection() {
      const active = state.activeSection;
      if (!active || state.activeStartedAt === null) return;
      const elapsed = Math.max(0, performance.now() - state.activeStartedAt);
      state.pendingAttentionMs.set(active.key, (state.pendingAttentionMs.get(active.key) || 0) + elapsed);
      state.activeStartedAt = null;
    }

    function flushAttention(item) {
      if (!item) return;
      const activeMs = state.pendingAttentionMs.get(item.key) || 0;
      if (activeMs < MIN_ATTENTION_SEGMENT_MS) return;
      state.pendingAttentionMs.delete(item.key);
      publishContentAttention(item, Math.min(activeMs, 120000));
    }

    function stopActiveSection({ flush = true } = {}) {
      const active = state.activeSection;
      pauseActiveSection();
      if (flush) flushAttention(active);
      state.activeSection = null;
    }

    function reconcileAttention() {
      if (document.visibilityState !== 'visible') {
        stopActiveSection();
        return;
      }

      const candidates = state.sections
        .filter((item) => state.tracked.has(item.key))
        .map((item) => ({ item, area: visibleArea(item.section) }))
        .filter((candidate) => candidate.area > 0 && hasMeaningfulVisibility(candidate.item.section))
        .sort((left, right) => right.area - left.area || left.item.index - right.item.index);
      const next = candidates[0]?.item || null;

      if (state.activeSection?.key === next?.key) {
        if (next && state.activeStartedAt === null) state.activeStartedAt = performance.now();
        return;
      }

      stopActiveSection();
      state.activeSection = next;
      state.activeStartedAt = next ? performance.now() : null;
    }

    function qualify(section) {
      const item = state.sections.find((candidate) => candidate.section === section);
      if (!item || state.tracked.has(item.key) || !hasMeaningfulVisibility(section)) {
        cancelTimer(section);
        return;
      }
      if (state.timers.has(section)) return;

      const timer = window.setTimeout(() => {
        state.timers.delete(section);
        const current = state.sections.find((candidate) => candidate.section === section);
        if (!current || state.tracked.has(current.key) || !hasMeaningfulVisibility(section)) return;
        state.tracked.add(current.key);
        persistTrackedSections();
        root.dataset.mbContentSectionsTracked = String(state.tracked.size);
        root.dataset.mbContentLastSection = current.key;
        publishContentView(current);
        reconcileAttention();
      }, VIEW_THRESHOLD_MS);
      state.timers.set(section, timer);
    }

    function rebuild() {
      state.observer?.disconnect();
      stopActiveSection();
      state.timers.forEach((timer) => window.clearTimeout(timer));
      state.timers.clear();

      const contentRoot = root.closest('main') || document.querySelector('main') || document.body;
      const sections = [...contentRoot.querySelectorAll('[data-mb-pdp-sku-section]')]
        .filter((section) => !root.contains(section))
        .filter((section) => Boolean(root.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING))
        .filter(sectionIsEligible);

      state.sections = sections.map((section, index) => ({
        section,
        key: sectionKey(section, index),
        label: sectionLabel(section, index),
        version: sectionVersion(section),
        index: index + 1,
        count: sections.length,
      }));
      root.dataset.mbContentSectionCount = String(sections.length);
      if (!state.sections.length || !('IntersectionObserver' in window)) return;

      state.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasMeaningfulVisibility(entry.target)) {
            qualify(entry.target);
          } else {
            cancelTimer(entry.target);
          }
        });
        reconcileAttention();
      }, { threshold: [0, 0.1, 0.25, 0.5, 0.75] });

      state.sections.forEach((item) => {
        state.observer.observe(item.section);
        if (hasMeaningfulVisibility(item.section)) qualify(item.section);
      });
    }

    document.addEventListener('mb:pdp:variant-change', (event) => {
      state.selectedVariant = event.detail?.variant || state.selectedVariant;
      window.requestAnimationFrame(() => window.requestAnimationFrame(rebuild));
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        stopActiveSection();
        state.timers.forEach((timer) => window.clearTimeout(timer));
        state.timers.clear();
        return;
      }
      state.sections.forEach((item) => qualify(item.section));
      reconcileAttention();
    });
    window.addEventListener('resize', () => {
      if (state.resizeRaf) window.cancelAnimationFrame(state.resizeRaf);
      state.resizeRaf = window.requestAnimationFrame(() => {
        state.resizeRaf = null;
        rebuild();
      });
    }, { passive: true });
    window.addEventListener('scroll', () => {
      if (state.attentionRaf) return;
      state.attentionRaf = window.requestAnimationFrame(() => {
        state.attentionRaf = null;
        reconcileAttention();
      });
    }, { passive: true });
    window.addEventListener('pagehide', () => {
      stopActiveSection();
      state.observer?.disconnect();
      state.timers.forEach((timer) => window.clearTimeout(timer));
      state.timers.clear();
      if (state.resizeRaf) window.cancelAnimationFrame(state.resizeRaf);
      if (state.attentionRaf) window.cancelAnimationFrame(state.attentionRaf);
      if (state.attentionFlushTimer) window.clearInterval(state.attentionFlushTimer);
    }, { once: true });

    state.attentionFlushTimer = window.setInterval(() => {
      const active = state.activeSection;
      pauseActiveSection();
      flushAttention(active);
      if (active && document.visibilityState === 'visible') state.activeStartedAt = performance.now();
    }, ATTENTION_FLUSH_MS);
    window.requestAnimationFrame(rebuild);
  }

  function initializeAll() {
    document.querySelectorAll('.mb-clean-pdp').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', initializeAll);
})();
